import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  Heading,
  VStack,
  Flex,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  FormControl,
  FormLabel,
  useToast,
  useColorMode,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  IconButton,
  Text,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormHelperText,
  FormErrorMessage,
  Stack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { MdEdit, MdVisibility, MdVisibilityOff, MdSave, MdCancel, MdPerson, MdEmail } from 'react-icons/md';
import { Helmet } from "react-helmet";
import axios from '../utils/axiosConfig';
import Navigation from '../components/Navigation';

const ProfilePage = () => {
  const { colorMode } = useColorMode();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
  });
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    role: '',
    company: '',
    phoneNumber: '',
  });
  const [error, setError] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/protected/profile');
      setUserData(response.data);
      setFormState(prev => ({
        ...prev,
        name: response.data.data.name || '',
        email: response.data.data.email || '',
      }));
    } catch (err) {
      setError(err.message);
      toast({
        title: 'Error',
        description: 'Failed to fetch user data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTogglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      // Only validate passwords if they are being changed
      if (formState.currentPassword || formState.newPassword) {
        if (!formState.currentPassword) {
          setError('Current password is required to change password');
          return;
        }
        if (!formState.newPassword) {
          setError('New password is required to change password');
          return;
        }
        if (formState.newPassword.length < 8) {
          setError('New password must be at least 8 characters long');
          return;
        }
      }

      const updateData = {
        name: formState.name,
        email: formState.email,
      };

      // Only include password fields if they are being changed
      if (formState.currentPassword && formState.newPassword) {
        updateData.currentPassword = formState.currentPassword;
        updateData.newPassword = formState.newPassword;
      }

      await axios.put('/api/protected/profile', updateData);
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully',
        status: 'success',
      });
      setIsEditing(false);
      setFormState(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
      }));
      
      // If password was changed, redirect to login page
      if (formState.currentPassword && formState.newPassword) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.status === 429) {
        toast({
          title: 'Rate limit reached',
          description: 'Please wait a few minutes before trying again',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else if (error.response?.status === 400 && error.response?.data?.message?.includes('Current password is incorrect')) {
        toast({
          title: 'Incorrect Password',
          description: 'The current password you entered is incorrect',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        setError(error.response?.data?.message || 'Failed to update profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await axios.delete('/api/protected/profile');
      toast({
        title: 'Success',
        description: 'Your account has been deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      // Redirect to login page or handle logout
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete account.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <>
      <Helmet>
        <title>Profile | AI Call System</title>
        <meta name="description" content="Manage your profile and account settings" />
      </Helmet>

      <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
        <Navigation />
        <Box flex="1" p={6}>
          <Flex justify="space-between" align="center" mb={6}>
            <Heading 
              size="lg" 
              color={colorMode === 'light' ? 'gray.800' : 'white'}
            >
              Profile
            </Heading>
            <Button
              leftIcon={isEditing ? <MdCancel /> : <MdEdit />}
              colorScheme={isEditing ? 'gray' : 'brand'}
              onClick={() => {
                if (isEditing) {
                  fetchUserData();
                }
                setIsEditing(!isEditing);
              }}
              isLoading={loading}
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </Button>
          </Flex>

          <Card bg={colorMode === 'light' ? 'white' : 'gray.800'}>
            <CardHeader>
              <Heading size="md">Profile Information</Heading>
            </CardHeader>
            <CardBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <MdPerson color="gray.300" />
                    </InputLeftElement>
                    <Input
                      name="name"
                      value={formState.name}
                      onChange={handleInputChange}
                      isReadOnly={!isEditing}
                      placeholder="Enter your name"
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <MdEmail color="gray.300" />
                    </InputLeftElement>
                    <Input
                      name="email"
                      type="email"
                      value={formState.email}
                      onChange={handleInputChange}
                      isReadOnly={!isEditing}
                      placeholder="Enter your email"
                    />
                  </InputGroup>
                </FormControl>

                {isEditing && (
                  <>
                    <Divider />
                    <Heading size="sm">Change Password</Heading>
                    <FormControl>
                      <FormLabel>Current Password</FormLabel>
                      <InputGroup>
                        <Input
                          name="currentPassword"
                          type={showPassword.currentPassword ? 'text' : 'password'}
                          value={formState.currentPassword}
                          onChange={handleInputChange}
                          placeholder="Enter current password"
                        />
                        <InputRightElement>
                          <IconButton
                            variant="ghost"
                            icon={showPassword.currentPassword ? <MdVisibilityOff /> : <MdVisibility />}
                            onClick={() => handleTogglePasswordVisibility('currentPassword')}
                            aria-label={showPassword.currentPassword ? 'Hide password' : 'Show password'}
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <FormControl>
                      <FormLabel>New Password</FormLabel>
                      <InputGroup>
                        <Input
                          name="newPassword"
                          type={showPassword.newPassword ? 'text' : 'password'}
                          value={formState.newPassword}
                          onChange={handleInputChange}
                          placeholder="Enter new password"
                        />
                        <InputRightElement>
                          <IconButton
                            variant="ghost"
                            icon={showPassword.newPassword ? <MdVisibilityOff /> : <MdVisibility />}
                            onClick={() => handleTogglePasswordVisibility('newPassword')}
                            aria-label={showPassword.newPassword ? 'Hide password' : 'Show password'}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormHelperText>
                        Password must be at least 8 characters long and include a mix of letters, numbers, and symbols.
                      </FormHelperText>
                    </FormControl>
                  </>
                )}
              </Stack>
            </CardBody>
            <CardFooter>
              <Flex justify="flex-end" width="100%">
                {isEditing && (
                  <Button
                    leftIcon={<MdSave />}
                    colorScheme="brand"
                    onClick={handleSave}
                    isLoading={loading}
                  >
                    Save Changes
                  </Button>
                )}
              </Flex>
            </CardFooter>
          </Card>

          {/* Delete Account Modal */}
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Delete Account</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Alert status="error" mb={4}>
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Warning!</AlertTitle>
                    <AlertDescription>
                      This action cannot be undone. All your data will be permanently deleted.
                    </AlertDescription>
                  </Box>
                </Alert>
                <Text>
                  Are you sure you want to delete your account? This will permanently remove all your data and cannot be recovered.
                </Text>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleDeleteAccount}
                  isLoading={loading}
                >
                  Delete Account
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      </Flex>
    </>
  );
};

export default ProfilePage;
